import { useMemo } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { Search, Star, Calculator, Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { ReportField, AggregationType } from './types'
import { AGGREGATIONS, POPULAR_FIELDS } from './types'

interface FieldPaletteProps {
  dataSource: string
  fields: ReportField[]
  fieldSearch: string
  selectedCount: number
  fieldsLoading: boolean
  open: boolean
  onOpenChange: (open: boolean) => void
  onFieldSearchChange: (search: string) => void
  onToggleField: (fieldId: string) => void
  onAggregationChange: (fieldId: string, aggregation: AggregationType) => void
  onSelectPopular: () => void
}

function getAvailableAggregations(fieldType: string) {
  return AGGREGATIONS.filter((agg) => !agg.numericOnly || fieldType === 'number')
}

function FieldItem({
  field,
  onToggle,
  onAggregationChange,
}: {
  field: ReportField
  onToggle: (id: string) => void
  onAggregationChange: (id: string, agg: AggregationType) => void
}) {
  return (
    <div
      className={`p-3 border rounded-lg transition-colors ${
        field.selected ? 'bg-primary/10 border-primary' : 'hover:bg-muted'
      }`}
    >
      <div className="flex items-center justify-between gap-4">
        <div
          className="flex-1 cursor-pointer"
          onClick={() => onToggle(field.id)}
          role="checkbox"
          aria-checked={field.selected}
          aria-label={`Select field ${field.label}`}
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              onToggle(field.id)
            }
          }}
        >
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">{field.label}</span>
            <Badge variant="outline" className="text-xs">
              {field.type}
            </Badge>
            {field.selected && (
              <Badge variant="default" className="text-xs">
                Selected
              </Badge>
            )}
          </div>
          <span className="text-xs text-muted-foreground">{field.name}</span>
        </div>
        {field.selected && (
          <div className="flex items-center gap-2">
            <Label htmlFor={`agg-${field.id}`} className="text-xs whitespace-nowrap">
              Aggregation:
            </Label>
            <span id={`agg-desc-${field.id}`} className="sr-only">
              Summarize this field's values, for example total revenue or average deal size
            </span>
            <Select
              value={field.aggregation}
              onValueChange={(value) => onAggregationChange(field.id, value as AggregationType)}
            >
              <SelectTrigger
                id={`agg-${field.id}`}
                className="w-32 h-8"
                aria-describedby={`agg-desc-${field.id}`}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {getAvailableAggregations(field.type).map((agg) => (
                  <SelectItem key={agg.value} value={agg.value}>
                    {agg.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>
    </div>
  )
}

export function FieldPalette({
  dataSource,
  fields,
  fieldSearch,
  selectedCount,
  fieldsLoading,
  open,
  onOpenChange,
  onFieldSearchChange,
  onToggleField,
  onAggregationChange,
  onSelectPopular,
}: FieldPaletteProps) {
  const popularFieldNames = useMemo(() => POPULAR_FIELDS[dataSource] || [], [dataSource])

  const filteredFields = useMemo(() => {
    if (!fieldSearch.trim()) return fields
    const search = fieldSearch.toLowerCase()
    return fields.filter(
      (f) => f.label.toLowerCase().includes(search) || f.name.toLowerCase().includes(search)
    )
  }, [fields, fieldSearch])

  const { popularFields, otherFields } = useMemo(() => {
    const popular: ReportField[] = []
    const other: ReportField[] = []
    filteredFields.forEach((field) => {
      if (popularFieldNames.includes(field.name)) {
        popular.push(field)
      } else {
        other.push(field)
      }
    })
    popular.sort((a, b) => popularFieldNames.indexOf(a.name) - popularFieldNames.indexOf(b.name))
    return { popularFields: popular, otherFields: other }
  }, [filteredFields, popularFieldNames])

  const hasAggregations = fields.some((f) => f.selected && f.aggregation !== 'none')

  return (
    <Card>
      <Collapsible open={open} onOpenChange={onOpenChange}>
        <CollapsibleTrigger className="w-full" disabled={!dataSource}>
          <CardHeader
            className={cn(
              'flex flex-row items-center justify-between p-4 cursor-pointer rounded-t-lg',
              dataSource ? 'hover:bg-muted/50' : 'opacity-50 cursor-not-allowed'
            )}
          >
            <div className="flex items-center gap-2">
              <CardTitle className="text-base">Fields & Aggregations</CardTitle>
              {selectedCount > 0 && (
                <Badge variant="secondary" className="h-5 px-1.5 text-xs">
                  {selectedCount}
                </Badge>
              )}
            </div>
          </CardHeader>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <CardContent className="pt-0 space-y-4">
            <div className="flex items-center justify-between gap-4 mb-4">
              <div className="flex-1 max-w-sm relative">
                <Search
                  className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground"
                  aria-hidden="true"
                />
                <Input
                  placeholder="Search fields..."
                  value={fieldSearch}
                  onChange={(e) => onFieldSearchChange(e.target.value)}
                  className="pl-9"
                  aria-label="Search fields"
                />
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onSelectPopular}
                  disabled={!popularFieldNames.length}
                  aria-label="Select popular fields"
                >
                  <Star className="mr-2 h-4 w-4" aria-hidden="true" />
                  Select Popular
                </Button>
                <span className="text-sm text-muted-foreground">{selectedCount} selected</span>
              </div>
            </div>

            {selectedCount > 0 && (
              <p className="text-xs text-muted-foreground mb-4" aria-live="polite">
                <Calculator className="inline h-3 w-3 mr-1" aria-hidden="true" />
                {hasAggregations ? (
                  <span className="text-[#B45309]">
                    Aggregations enabled — choose a Group By field in Visualization below.
                  </span>
                ) : (
                  <span>
                    Use the Aggregation dropdown on selected fields to summarize values (e.g., sum
                    of revenue, count of deals).
                  </span>
                )}
              </p>
            )}

            {fieldsLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2
                  className="h-6 w-6 animate-spin text-muted-foreground"
                  aria-hidden="true"
                />
              </div>
            ) : (
              <div className="space-y-4">
                {popularFields.length > 0 && !fieldSearch && (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Star className="h-4 w-4 text-[#B45309]" aria-hidden="true" />
                      <span className="text-sm font-medium">Popular Fields</span>
                    </div>
                    <div className="space-y-2">
                      {popularFields.map((field) => (
                        <FieldItem
                          key={field.id}
                          field={field}
                          onToggle={onToggleField}
                          onAggregationChange={onAggregationChange}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {(fieldSearch ? filteredFields : otherFields).length > 0 && (
                  <div>
                    {!fieldSearch && otherFields.length > 0 && (
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-sm font-medium text-muted-foreground">
                          All Fields
                        </span>
                      </div>
                    )}
                    <div className="space-y-2">
                      {(fieldSearch ? filteredFields : otherFields).map((field) => (
                        <FieldItem
                          key={field.id}
                          field={field}
                          onToggle={onToggleField}
                          onAggregationChange={onAggregationChange}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {filteredFields.length === 0 && fieldSearch && (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    No fields match &quot;{fieldSearch}&quot;
                  </p>
                )}
              </div>
            )}
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  )
}
